import { Check, Visibility, VisibilityOff } from "@mui/icons-material";
import { AlertColor, Box, Button, Divider, FormControl, Grid, IconButton, Input, InputAdornment, InputLabel, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditIcon } from "../../../../assets/icons/editIcon";
import { useProfile } from "../../../../context/ProfileProvider/useProfile";
import { ChangePasswordLoggedValueObject, ChangePasswordValueObject } from "../../../../model/IdentitiModel";
import { ChangePasswordAccess, ChangePasswordLogged } from "../../../../services/IdentitiServices";
import { REGEX_CONSTANTS } from "../../../User/NewPassword/components/RealTimePasswordCheck/Regex/RegexUtils";

export interface IDataRenderPassword {
    setPopupOk: (set: boolean) => void;
    setAlertColor: (set: AlertColor) => void;
    setMessage: (set: string) => void;
}

export const DataRenderPassword: React.FC<IDataRenderPassword> = ({
    setAlertColor,
    setMessage,
    setPopupOk
}) => {

    const [actualPassword, setActualPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [ShowActualPassword, setShowActualPassword] = useState<boolean>(false);
    const [ShowNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [ShowConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [canChangePassword, setCanChangePassword] = useState<boolean>(false);
    const { NUMBER_REGEX, SPECIAL_REGEX, UPPERCASE_REGEX } = REGEX_CONSTANTS;
    const { t } = useTranslation();
    const profile = useProfile();

    function PasswordLengthValidator(password: string) {
        if (password.length < 6) {
            setPopupOk(true)
            setAlertColor("error")
            setMessage(t("PasswordMessage.MinLength"))
            throw new Error();
        };
        return true;
    }

    function PasswordNumberValidation(password: string) {
        if (!NUMBER_REGEX.test(password)) {
            setPopupOk(true)
            setAlertColor("error")
            setMessage(t("PasswordMessage.AtLeastOneNumber"))
            throw new Error();
        }
        return true
    }

    function PasswordSpecialValidation(password: string) {
        if (!SPECIAL_REGEX.test(password)) {
            setPopupOk(true)
            setAlertColor("error")
            setMessage(t("PasswordMessage.EspecialValidation"))
            throw new Error();
        }
        return true
    }

    function PasswordUppercaseValidation(password: string) {
        if (!UPPERCASE_REGEX.test(password)) {
            setPopupOk(true)
            setAlertColor("error")
            setMessage(t("PasswordMessage.UppercaseValidation"))
            throw new Error();
        }
        return true
    }

    function PasswordConfirmValidation(password: string, confirmPass: string) {
        if (newPassword != confirmPassword) {
            setPopupOk(true)
            setAlertColor("error")
            setMessage(t("PasswordMessage.DiffPass"))
            throw new Error();
        }
        return true
    }

    function validator() {
        PasswordLengthValidator(newPassword)
        PasswordLengthValidator(newPassword);
        PasswordNumberValidation(newPassword);
        PasswordSpecialValidation(newPassword);
        PasswordUppercaseValidation(newPassword);
        PasswordConfirmValidation(newPassword, confirmPassword);

        return true;
    }

    return (
        <Grid item xs={12}>
            <Box
                marginTop="18px"
            >
                <Typography
                    fontWeight="600"
                    fontSize="14px"
                    color="#2D2D2D"
                >
                    {t("MyData.Pass")}
                </Typography>
            </Box>
            <Box
                display="flex"
                alignItems="end"
                gap="20px"
            >
                <Box
                    marginTop="20px"
                    flex={1.5}
                >
                    {canChangePassword ? (
                        <Box
                            display="flex"
                            gap={2}
                        >
                            <Box
                                width="100%"
                            >
                                <FormControl sx={{ width: '100%' }} variant="standard">
                                    <InputLabel htmlFor="standard-adornment-password">{t("MyData.NewPassword")}</InputLabel>
                                    <Input
                                        onChange={e => setNewPassword(e.currentTarget.value)}
                                        value={newPassword}
                                        id="standard-adornment-password"
                                        type={ShowNewPassword ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    sx={{
                                                        color: "#22638D",
                                                        opacity: "0.2"
                                                    }}
                                                    aria-label={
                                                        ShowNewPassword ? 'hide the password' : 'display the password'
                                                    }
                                                    onClick={() => setShowNewPassword(!ShowNewPassword)}
                                                >
                                                    {ShowNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </Box>
                            <Box
                                width="100%"
                            >
                                <FormControl sx={{ width: '100%' }} variant="standard">
                                    <InputLabel htmlFor="standard-adornment-password">{t("MyData.ConfirmPassword")}</InputLabel>
                                    <Input
                                        onChange={e => setConfirmPassword(e.currentTarget.value)}
                                        value={confirmPassword}
                                        id="standard-adornment-password"
                                        type={ShowConfirmPassword ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    sx={{
                                                        color: "#22638D",
                                                        opacity: "0.2"
                                                    }}
                                                    aria-label={
                                                        ShowConfirmPassword ? 'hide the password' : 'display the password'
                                                    }
                                                    onClick={() => setShowConfirmPassword(!ShowConfirmPassword)}
                                                >
                                                    {ShowConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <FormControl sx={{ width: '100%' }} variant="standard">
                                <InputLabel htmlFor="standard-adornment-password">{t("MyData.CurrentPass")}</InputLabel>
                                <Input
                                    autoComplete="new-password" 
                                    onChange={e => setActualPassword(e.currentTarget.value)}
                                    value={actualPassword}
                                    id="standard-adornment-password"
                                    type={ShowActualPassword ? 'text' : 'password'}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                sx={{
                                                    color: "#22638D",
                                                    opacity: "0.2"
                                                }}
                                                aria-label={
                                                    ShowActualPassword ? 'hide the password' : 'display the password'
                                                }
                                                onClick={() => setShowActualPassword(!ShowActualPassword)}
                                            >
                                                {ShowActualPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>

                        </>
                    )}
                </Box>
                {canChangePassword ? (
                    <Box
                        alignItems="end"
                        display="flex"
                        gap={2}
                        flex={1}
                    >
                        <Button
                            fullWidth
                            variant="outlined"
                            sx={{
                                border: "1px solid #22638d",
                                color: "#22638d",
                                marginRight: "10px",
                                textTransform: "none",
                                fontWeight: "normal",
                                padding: "6px 10px"
                            }}
                            onClick={() => setCanChangePassword(false)}
                        >
                            {t("MyData.Cancel")}
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Check />}
                            sx={{
                                background: "#22638D",
                                fontWeight: "normal",
                                padding: "6px 10px",
                                textTransform: "none",
                                '&:hover': {
                                    background: "#467ca0"
                                }
                            }}
                            onClick={e => {
                                if (validator()) {
                                    let changePasswordLoggedValueObject: ChangePasswordLoggedValueObject = {
                                        confirmPassword: confirmPassword,
                                        newPassword: newPassword,
                                        userEmail: profile.login!
                                    }
                                    ChangePasswordLogged(changePasswordLoggedValueObject)
                                        .then(() => {
                                            setCanChangePassword(false);
                                            setAlertColor("success");
                                            setPopupOk(true);
                                            setMessage(t("PasswordMessage.changedSuccessfully"));
                                        }).catch((error) => {
                                            setCanChangePassword(true);
                                            setAlertColor("error");
                                            setPopupOk(true);
                                            setMessage(error.response.data);
                                        })
                                }
                            }}
                        >
                            {t("MyData.ChangePass")}
                        </Button>
                    </Box>
                ) : (
                    <Box
                        flex={1}
                        alignItems="end"
                        display="flex"
                        marginLeft="20px"
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<EditIcon color="#FFFFFF" />}
                            sx={{
                                background: "#22638D",
                                fontWeight: "normal",
                                padding: "6px 10px",
                                textTransform: "none",
                                '&:hover': {
                                    background: "#467ca0"
                                }
                            }}
                            onClick={e => {
                                let changePasswordAccess: ChangePasswordValueObject = {
                                    actualPassword: actualPassword,
                                    userEmail: profile.login!
                                }
                                ChangePasswordAccess(changePasswordAccess)
                                    .then(() => {
                                        setActualPassword("");
                                        setCanChangePassword(true)
                                    })
                                    .catch((error) => {
                                        console.log(error.response.data)
                                        setCanChangePassword(false);
                                        setAlertColor("error");
                                        setPopupOk(true);
                                        setMessage(error.response.data);
                                    })
                            }}
                        >
                            {t("MyData.ChangePass")}
                        </Button>
                    </Box>
                )}
            </Box>
            <Divider
                sx={{
                    marginTop: "20px",
                    opacity: .2,
                    borderColor: "#22638D",

                }}
            />
        </Grid>
    )
}